import FieldConfigurationAPI from "../../../../../lib/api/FieldConfiguration";

// Mirrors the "which company keeps this role" logic
// FieldConfigurationContext#saveRoleWithCompanyAssignments uses internally.
export function getCompanyRoleTargets(
  role,
  selectedCompanyUuids,
  showCompanySection
) {
  const companyUuids = role?.companyUuids || [];
  const roleUuids = role?.uuids || (role?.uuid ? [role.uuid] : []);

  if (!showCompanySection) {
    return companyUuids.map((companyUuid, i) => ({
      companyUuid,
      roleUuid: roleUuids[i] || role?.uuid,
    }));
  }

  const selectedSet = new Set(selectedCompanyUuids || []);
  return companyUuids
    .map((companyUuid, i) => ({
      companyUuid,
      roleUuid: roleUuids[i] || role?.uuid,
    }))
    .filter((target) => selectedSet.has(target.companyUuid));
}

// Approval Teams with a member whose role is the one being edited. Throws
// on any fetch failure so callers don't treat it as "no impact".
export async function findApprovalTeamImpact(companyRoleTargets) {
  const teamsToTrim = [];
  const teamsToDelete = [];
  const teamsByCompany = {};

  await Promise.all(
    (companyRoleTargets || []).map(async ({ companyUuid, roleUuid }) => {
      if (!companyUuid || !roleUuid) return;

      const usersResponse = await FieldConfigurationAPI.getUsers(companyUuid);
      if (usersResponse.status !== 200 || !Array.isArray(usersResponse.data)) {
        throw new Error(`Failed to fetch users for company ${companyUuid}`);
      }
      const affectedUserUuids = new Set(
        usersResponse.data
          .filter((u) => u.role_uuid === roleUuid)
          .map((u) => u.uuid)
      );

      if (affectedUserUuids.size === 0) return;

      const teamsResponse = await FieldConfigurationAPI.getApprovalTeams(
        companyUuid,
        { limit: 1000 }
      );
      if (teamsResponse.status !== 200 || !Array.isArray(teamsResponse.data)) {
        throw new Error(
          `Failed to fetch approval teams for company ${companyUuid}`
        );
      }
      const teams = teamsResponse.data;
      teamsByCompany[companyUuid] = teams.map((team) => ({
        ...team,
        company_uuid: companyUuid,
      }));

      teams.forEach((team) => {
        const memberUuids = (team.company_users || []).map((u) =>
          typeof u === "string" ? u : u.uuid
        );
        const remainingUuids = memberUuids.filter(
          (uuid) => !affectedUserUuids.has(uuid)
        );

        if (remainingUuids.length === memberUuids.length) return; // no overlap

        if (remainingUuids.length > 0) {
          teamsToTrim.push({
            team: { ...team, company_uuid: companyUuid },
            remainingUuids,
            removedCount: memberUuids.length - remainingUuids.length,
          });
        } else {
          teamsToDelete.push({ team: { ...team, company_uuid: companyUuid } });
        }
      });
    })
  );

  return { teamsToTrim, teamsToDelete, teamsByCompany };
}
